import { useState } from "react";
import { Box, Divider, Typography } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import {
    KeyboardArrowLeftRounded,
    KeyboardArrowRightRounded,
} from "@mui/icons-material";

import { OrganizationConfiguration } from "./components/OrganizationConfiguration";
import { PrimaryAdmin, DEFAULT_PRIMARY_ADMIN } from "./components/PrimaryAdmin";
import useCreateOrganization from "./hooks/useCreateOrganization";
import newOrganizationResolver from "../../../../libs/form-validations/organization";
import Breadcrumb from "./components/Breadcrumb";

import { Alert, Button, ProgressStepper } from "../../../../shared";
import { Billing } from "../../..";
import { DEFAULT_BILLING_SELECTION } from "../../../Billing/constants";
import { useAuthorization } from "../../../../hooks";
import { ROLES } from "../../../../constants/permissions";

import type {
    IBillingSelection,
    IPrimaryAdminInvite,
} from "../../../../interfaces";

const STEPS = [
    { label: "Basic Details" },
    { label: "Billing" },
    { label: "Primary Admin" },
];

const STEP_DESCRIPTIONS = [
    "Add the organization profile details used across the platform.",
    "Choose how this organization will be billed for using the platform.",
    "Invite the person who will own and administer this organization.",
];

const INITIAL_VALUES = {
    Name: "",
    ShortName: "",
    Description: "",
    Website: "",
    LogoUrl: "",
    LogoFile: null,
    IsActive: true,
    ContractTemplateId: "standard",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isBasicDetailsValid(name: string, website: string) {
    if (!name.trim()) return false;
    const site = website.trim();
    return site === "" || /^https?:\/\//i.test(site);
}

function isBillingValid(billing: IBillingSelection) {
    if (
        billing.BillingEmail.trim() &&
        !EMAIL_PATTERN.test(billing.BillingEmail.trim())
    ) {
        return false;
    }

    switch (billing.Mode) {
        case "TRIAL":
            return Number(billing.TrialDays) > 0;
        case "SUBSCRIPTION":
            return (
                Number(billing.Seats) > 0 && billing.RatePerSeat.trim() !== ""
            );
        case "PER_PROJECT":
            return billing.RatePerProject.trim() !== "";
        case "CUSTOM":
            return billing.ContractReference.trim() !== "";
        default:
            return false;
    }
}

function isPrimaryAdminValid(invite: IPrimaryAdminInvite) {
    if (invite.SkipInvite) return true;
    return (
        invite.FirstName.trim() !== "" &&
        invite.LastName.trim() !== "" &&
        EMAIL_PATTERN.test(invite.Email.trim())
    );
}

export default function Create() {
    const { hasRole } = useAuthorization();
    const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN);
    const { handleCreateOrganization, isPending, errorMessage, successMessage } =
        useCreateOrganization();
    const [currentStep, setCurrentStep] = useState(0);
    const [billing, setBilling] = useState<IBillingSelection>(
        DEFAULT_BILLING_SELECTION,
    );
    const [primaryAdmin, setPrimaryAdmin] = useState<IPrimaryAdminInvite>(
        DEFAULT_PRIMARY_ADMIN,
    );

    const methods = useForm({
        mode: "onChange",
        resolver: newOrganizationResolver,
        defaultValues: INITIAL_VALUES,
    });

    const { handleSubmit, watch } = methods;
    const name = watch("Name") || "";
    const website = watch("Website") || "";
    const isLastStep = currentStep === STEPS.length - 1;

    const stepValid =
        currentStep === 0
            ? isBasicDetailsValid(name, website)
            : currentStep === 1
              ? isBillingValid(billing)
              : isPrimaryAdminValid(primaryAdmin);

    const onSubmit = handleSubmit((data) => handleCreateOrganization(data, primaryAdmin));

    const handleNext = () => {
        if (!stepValid) return;
        setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    };

    const handlePrevious = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleStepClick = (step: { label?: string }) => {
        const index = STEPS.findIndex((item) => item.label === step.label);
        if (index >= 0 && index <= currentStep) {
            setCurrentStep(index);
        }
    };

    if (!isSuperAdmin) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Only Super Admin can create organizations." />
            </Box>
        );
    }

    return (
        <Box flex={1}>
            <Breadcrumb />

            <Divider />

            <Box p={3} pb={0}>
                <Typography level="title-lg">New Organization</Typography>
                <Typography level="body-sm">
                    {STEP_DESCRIPTIONS[currentStep]}
                </Typography>
            </Box>

            <Box component="div" ml={3} width={700} pb={3}>
                <FormProvider {...methods}>
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!isLastStep && stepValid) {
                                handleNext();
                            }
                        }}
                    >
                        <Box mt={2}>
                            <ProgressStepper
                                currentStep={currentStep}
                                steps={STEPS}
                                onClick={handleStepClick}
                            />
                        </Box>

                        <Box
                            mt={2}
                            display="flex"
                            gap={2}
                            flexDirection="column"
                        >
                            {currentStep === 0 && <OrganizationConfiguration />}
                            {currentStep === 1 && (
                                <Billing
                                    value={billing}
                                    onChange={setBilling}
                                />
                            )}
                            {currentStep === 2 && (
                                <PrimaryAdmin
                                    value={primaryAdmin}
                                    onChange={setPrimaryAdmin}
                                />
                            )}

                            <Box display="flex" gap={1} mt={1}>
                                {currentStep === 0 ? (
                                    <Button
                                        type="button"
                                        variant="soft"
                                        color="neutral"
                                        component={Link}
                                        to="/admin/organizations"
                                        disabled={isPending}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="soft"
                                        color="neutral"
                                        startDecorator={
                                            <KeyboardArrowLeftRounded />
                                        }
                                        onClick={handlePrevious}
                                        disabled={isPending}
                                    >
                                        Previous
                                    </Button>
                                )}

                                {!isLastStep ? (
                                    <Button
                                        type="button"
                                        endDecorator={
                                            <KeyboardArrowRightRounded />
                                        }
                                        onClick={handleNext}
                                        disabled={!stepValid || isPending}
                                    >
                                        Continue
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        loading={isPending}
                                        disabled={!stepValid || isPending}
                                        onClick={() => void onSubmit()}
                                    >
                                        Create Organization
                                    </Button>
                                )}
                            </Box>

                            <Alert
                                successMessage={successMessage}
                                sx={{ mt: 1 }}
                                errorMessage={errorMessage}
                            />
                        </Box>
                    </form>
                </FormProvider>
            </Box>
        </Box>
    );
}
